import {
  createContext, useCallback, useContext, useEffect, useId, useRef, useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { X, ChevronDown, Check, MoreHorizontal } from 'lucide-react'
import { Floating } from '../shell/floating.js'
import { useOverlay } from '../shell/overlays.js'
import { usePresence } from '../shell/presence.js'
import { requestStepUp } from '../shell/stepup-bus.js'
import { onUnauthenticated } from '../shell/auth-routing.js'

/**
 * PRIMITIVES du design system introduites au LOT 4 — chacune construite ici
 * devient LA référence (documentées dans le design system après validation) :
 *   · lc-drawer  — panneau latéral (référence : détail d'une alerte)
 *   · lc-select  — sélecteur accessible clavier
 *   · lc-tabs    — onglets (référence : fiche utilisateur)
 *   · lc-toast   — retour d'action bref, non bloquant
 */

/* ═══════════════════════════════ DRAWER ═══════════════════════════════ */

export function Drawer(props: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode | undefined
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  // Sortie animée (LOT 5.5.2) : le panneau reste monté le temps du fondu.
  const presence = usePresence(props.open, 200)

  useEffect(() => {
    if (!props.open) return
    const previous = document.activeElement as HTMLElement | null
    panelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onClose() }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus()
    }
  }, [props.open]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!presence.mounted) return null
  // PORTAL (v4) : un drawer peut être ouvert depuis un widget du Layout
  // Engine (overflow hidden, contexte de transform pendant le drag) — il vit
  // donc toujours au niveau du document, comme le Select et les Dialog.
  return createPortal(
    <div className="lc-drawer-overlay" data-state={presence.state} onClick={props.onClose} role="presentation">
      <div
        ref={panelRef}
        className="lc-drawer"
        data-state={presence.state}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lc-drawer__head">
          <h2 className="lc-drawer__title">{props.title}</h2>
          <button className="lc-btn lc-btn--ghost lc-btn--icon" onClick={props.onClose} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>
        <div className="lc-drawer__body">{props.children}</div>
        {props.footer ? <footer className="lc-drawer__foot">{props.footer}</footer> : null}
      </div>
    </div>,
    document.body,
  )
}

/* ═══════════════════════════════ SELECT ═══════════════════════════════
   LOT 5.5.1 : la liste vit dans un PORTAL (module floating) — flip, shift,
   max-height + scroll interne. Elle ne peut plus être découpée par un parent
   `overflow: hidden` ni sortir du viewport. */

export interface SelectOption {
  value: string
  label: string
  /** Pastille d'état optionnelle (classe lc-dot--…) — LOT 9.0.2. */
  dot?: string | undefined
}

export function Select(props: {
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  label?: string | undefined
  compact?: boolean | undefined
  /** Affiché quand value ne correspond à aucune option (LOT 9.0.2). */
  placeholder?: string | undefined
  /** Habillage contextuel (ex. token du composer de condition). */
  className?: string | undefined
  ariaLabel?: string | undefined
}) {
  const [open, setOpen] = useState(false)
  const [focusIdx, setFocusIdx] = useState(-1)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listId = useId()
  const current = props.options.find((o) => o.value === props.value)

  const onKey = (e: React.KeyboardEvent) => {
    // Escape n'est PAS traité ici : c'est la pile d'overlays qui arbitre, et
    // elle ne ferme que la surface la plus haute. Deux composants qui
    // réagissent au même Escape, c'était la modale emportée avec le menu.
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (!open) { setOpen(true); setFocusIdx(props.options.findIndex((o) => o.value === props.value)); return }
      if (focusIdx >= 0) { props.onChange(props.options[focusIdx]!.value); setOpen(false) }
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) { setOpen(true); setFocusIdx(0); return }
      const delta = e.key === 'ArrowDown' ? 1 : -1
      setFocusIdx((i) => Math.max(0, Math.min(props.options.length - 1, i + delta)))
    }
  }

  return (
    <div className={`lc-select${props.compact ? ' lc-select--compact' : ''}${props.className ? ` ${props.className}` : ''}`} onKeyDown={onKey}>
      {props.label ? <span className="lc-label">{props.label}</span> : null}
      <button
        ref={triggerRef}
        type="button"
        className="lc-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={props.ariaLabel}
        onClick={() => setOpen(!open)}
      >
        <span className="lc-select__value">
          {current?.dot ? <span className={`lc-dot ${current.dot}`} aria-hidden="true" /> : null}
          {current?.label ?? <span className="lc-select__ph">{props.placeholder ?? '—'}</span>}
        </span>
        <ChevronDown size={15} className={`lc-select__chev${open ? ' lc-select__chev--open' : ''}`} aria-hidden="true" />
      </button>
      <Floating
        open={open}
        anchorRef={triggerRef}
        matchAnchorWidth
        onOutside={() => setOpen(false)}
        className="lc-select__panel"
      >
        <ul className="lc-select__list" role="listbox" id={listId} aria-activedescendant={focusIdx >= 0 ? `${listId}-${focusIdx}` : undefined}>
          {props.options.map((o, i) => (
            <li
              key={o.value}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={o.value === props.value}
              className={`lc-select__opt${i === focusIdx ? ' lc-select__opt--focus' : ''}`}
              onMouseEnter={() => setFocusIdx(i)}
              onClick={() => { props.onChange(o.value); setOpen(false) }}
            >
              <span className="lc-select__value">
                {o.dot ? <span className={`lc-dot ${o.dot}`} aria-hidden="true" /> : null}
                {o.label}
              </span>
              {o.value === props.value ? <Check size={14} aria-hidden="true" /> : null}
            </li>
          ))}
        </ul>
      </Floating>
    </div>
  )
}

/* ═══════════════════════════════ MENU DE LIGNE ═══════════════════════
   Les actions d'une fiche (scanner, modifier, désactiver, supprimer) ne
   tiennent pas en boutons côte à côte, et surtout : la destructive ne doit
   pas être à un pixel de la bénigne. Un ⋯ les regroupe, la dangereuse en bas
   et distincte. Bâti sur `Floating` — même positionnement que le Select,
   donc jamais découpé par un parent en overflow hidden. */

export interface RowMenuItem {
  label: string
  onClick: () => void
  icon?: ReactNode
  danger?: boolean
  disabled?: boolean
  /** Pourquoi l'action est indisponible — affiché, jamais silencieux. */
  reason?: string | undefined
}

export function RowMenu(props: { label: string; items: RowMenuItem[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  return (
    <>
      <button
        ref={ref} type="button" className="lc-btn lc-btn--icon"
        aria-haspopup="menu" aria-expanded={open} aria-label={props.label}
        onClick={() => setOpen((o) => !o)}
      >
        <MoreHorizontal size={16} />
      </button>
      <Floating open={open} anchorRef={ref} onOutside={() => setOpen(false)} className="lc-menu">
        <ul className="lc-menu__list" role="menu">
          {props.items.map((it) => (
            <li key={it.label} role="none">
              <button
                type="button" role="menuitem"
                className={`lc-menu__item${it.danger ? ' lc-menu__item--danger' : ''}`}
                disabled={it.disabled}
                title={it.disabled ? it.reason : undefined}
                onClick={() => { setOpen(false); it.onClick() }}
              >
                {it.icon}
                <span>{it.label}</span>
              </button>
              {it.disabled && it.reason ? <p className="lc-menu__why">{it.reason}</p> : null}
            </li>
          ))}
        </ul>
      </Floating>
    </>
  )
}

/* ═══════════════════════════════ MODAL ═══════════════════════════════
   LA modale LIVE CONTROL (LOT 5.5.1) — tâche focalisée : édition d'un rôle,
   création d'un compte, flow WebAuthn. Large (jusqu'à ~960 px), max-height
   viewport + scroll interne, focus piégé, Escape via la pile d'overlays.
   Une modale ÉDITABLE ne porte JAMAIS de bouton Enregistrer : son état dirty
   passe par la Save Bar globale (voir shell/editing.tsx).

   DEUX FAMILLES, une propriété pour les distinguer — voir `owning`. */

export function Modal(props: {
  open: boolean
  onClose: () => void
  /**
   * La modale POSSÈDE l'écran : elle passe devant la barre d'enregistrement.
   *
   * Par défaut une modale reste SOUS la barre, parce que beaucoup d'entre
   * elles sont des formulaires que la barre confirme — la masquer priverait
   * l'utilisateur de son bouton. Mais une modale qui porte son propre pied
   * (configuration d'une tuile) n'a rien à confirmer là-bas pendant qu'elle
   * est ouverte, et la barre lui recouvrait le bas du contenu.
   *
   * C'est la modale qui le déclare, pas une feuille de style de
   * fonctionnalité qui viendrait la reconnaître à sa classe.
   */
  owning?: boolean | undefined
  /** Appelé quand l'animation de SORTIE est terminée (démontage effectif). */
  onExited?: (() => void) | undefined
  ariaLabel: string
  width?: number
  children: ReactNode
  className?: string
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  useOverlay(props.open, props.onClose, { lockScroll: true })
  // Sortie animée (LOT 5.5.2) : opacity/scale/translate ~190 ms, la surface
  // ne quitte le DOM qu'à la fin — puis onExited previent l'appelant.
  const presence = usePresence(props.open, 200)
  const exitedRef = useRef(props.onExited)
  exitedRef.current = props.onExited
  useEffect(() => {
    if (!presence.mounted && !props.open) exitedRef.current?.()
  }, [presence.mounted]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!props.open) return
    const previous = document.activeElement as HTMLElement | null
    panelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (!focusables?.length) return
      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus()
    }
  }, [props.open]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!presence.mounted) return null
  return createPortal(
    <div className={`lc-modal-overlay${props.owning ? ' lc-modal-overlay--owning' : ''}`}
      data-state={presence.state} role="presentation" onClick={props.onClose}>
      <div
        ref={panelRef}
        className={`lc-modal${props.className ? ` ${props.className}` : ''}`}
        data-state={presence.state}
        role="dialog"
        aria-modal="true"
        aria-label={props.ariaLabel}
        tabIndex={-1}
        style={props.width ? { width: `min(${props.width}px, calc(100vw - 32px))` } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fondu d'accent : la couleur du thème ÉMANE du haut de la surface
            et se dissipe — principe repris de la Main Courante, habillage
            LIVE CONTROL (suit l'accent actif, custom compris). */}
        <span className="lc-modal__fade" aria-hidden="true" />
        {props.children}
      </div>
    </div>,
    document.body,
  )
}

/* ═══════════════════════════════ TABS ═══════════════════════════════ */

export function Tabs(props: {
  tabs: { key: string; label: string }[]
  active: string
  onChange: (key: string) => void
}) {
  const onKey = (e: React.KeyboardEvent) => {
    const idx = props.tabs.findIndex((t) => t.key === props.active)
    if (e.key === 'ArrowRight') props.onChange(props.tabs[(idx + 1) % props.tabs.length]!.key)
    if (e.key === 'ArrowLeft') props.onChange(props.tabs[(idx - 1 + props.tabs.length) % props.tabs.length]!.key)
  }
  return (
    <div className="lc-tabs" role="tablist" onKeyDown={onKey}>
      {props.tabs.map((t) => (
        <button
          key={t.key}
          role="tab"
          aria-selected={t.key === props.active}
          tabIndex={t.key === props.active ? 0 : -1}
          className={`lc-tabs__tab${t.key === props.active ? ' lc-tabs__tab--active' : ''}`}
          onClick={() => props.onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

/* ═══════════════════════════════ TOASTS ═══════════════════════════════ */

export interface ToastAction { label: string; onAction: () => void }
export type ToastFn = (kind: 'ok' | 'error', text: string, opts?: { action?: ToastAction; durationMs?: number }) => void
interface Toast { id: number; kind: 'ok' | 'error'; text: string; action?: ToastAction }
const ToastCtx = createContext<ToastFn>(() => undefined)

export function useToast(): ToastFn {
  return useContext(ToastCtx)
}

export function ToastProvider(props: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), [])
  const push = useCallback<ToastFn>((kind, text, opts) => {
    const id = nextId.current++
    setToasts((t) => [...t, { id, kind, text, ...(opts?.action ? { action: opts.action } : {}) }])
    // 4,5 s pour lire ; 7 s si une action (ANNULER) est offerte — le temps d'y revenir.
    const ttl = opts?.durationMs ?? (opts?.action ? 7000 : 4500)
    setTimeout(() => dismiss(id), ttl)
  }, [dismiss])
  // PORTAL vers <body> (LOT 4.6) : `.lc-route` porte un transform d'entrée qui
  // crée un stacking context — un toast rendu dans l'arbre de la page pouvait
  // se retrouver DANS le flux du planning. Hors de tout conteneur, bas-droite.
  const host = typeof document !== 'undefined' ? document.body : null
  const toastsEl = (
    <div className="lc-toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`lc-toast lc-toast--${t.kind}${t.action ? ' lc-toast--action' : ''}`}>
          <span>{t.text}</span>
          {t.action ? (
            <button className="lc-toast__action" onClick={() => { t.action!.onAction(); dismiss(t.id) }}>{t.action.label}</button>
          ) : null}
        </div>
      ))}
    </div>
  )
  return (
    <ToastCtx.Provider value={push}>
      {props.children}
      {host ? createPortal(toastsEl, host) : toastsEl}
    </ToastCtx.Provider>
  )
}

/* ═══════════════ aides d'appel API (session + CSRF) ═══════════════ */

let csrfToken: string | null = null

/**
 * OPTIONS D'APPEL (Signalements, 09/10/2026) — génériques, pour tout module.
 *
 * Un module qui parle à un service par le serveur de GESTION a parfois besoin
 * d'en-têtes à lui : une clé d'idempotence (`Idempotency-Key`), le nom d'un
 * fichier déposé en octets bruts (`X-File-Name`). Ils COMPLÈTENT ceux du
 * client, ils ne les remplacent pas : le jeton CSRF reste posé ici, en
 * dernier, et un corps JSON garde son `content-type`. Seul un dépôt binaire
 * peut changer le sien (un PDF part en `application/octet-stream`, que le
 * serveur sait lire, plutôt qu'en `application/pdf`, qu'il refuserait).
 *
 * `deconnexionExplicite` : seul un 401 qui DIT être la fin de la session
 * GESTION (`error: 'UNAUTHENTICATED'`) déconnecte. Sans l'option, un 401 sans
 * code déconnecte aussi (comportement historique, inchangé) ; avec elle, il est
 * rendu à l'appelant comme une erreur ordinaire. Un service amont qui refuse
 * le serveur de GESTION n'a pas à faire sortir l'utilisateur de GESTION.
 */
export interface ApiOptions {
  headers?: Record<string, string> | undefined
  deconnexionExplicite?: boolean | undefined
  /** Aucun renvoi automatique vers la connexion sur un 401 : l'appelant décide (page « Autoriser LC Porte »). */
  sansRedirection?: boolean | undefined
}

/** LOT 6 — réaction transversale aux fins de session / interdits.
 *  Jamais sur les routes d'authentification elles-mêmes (un 401 y est un
 *  résultat normal : mauvais identifiants, code refusé…). */
function reactToStatus(path: string, status: number, error?: string, options?: ApiOptions): void {
  if (path.startsWith('/api/v1/auth/')) return
  if (options?.sansRedirection) return
  const finDeSession = error === 'UNAUTHENTICATED' || (error === undefined && !options?.deconnexionExplicite)
  if (status === 401 && finDeSession) onUnauthenticated()
  // UN 403 NE DÉTOURNE PLUS LA PAGE ENTIÈRE.
  //
  // Il le faisait, et c'est ce qui rendait « Accès refusé » intermittent pour
  // un Super Admin : le premier appel secondaire refusé emportait tout le
  // document, quel que soit le droit réel sur la page. Un widget légitimement
  // interdit produisait le même effet.
  //
  // Le refus est désormais rendu à l'appelant, qui l'affiche à SA place. Seule
  // une interdiction de la ROUTE mène à /access-denied, via `RequirePermission`.
  // Le serveur, lui, n'a pas changé d'un iota : le RBAC reste où il est.
}

export async function apiMutate<T>(method: string, path: string, body?: unknown, options?: ApiOptions): Promise<{ status: number; data: T }> {
  if (!csrfToken) {
    const s = await fetch('/api/v1/auth/session', { credentials: 'same-origin' }).then((r) => r.json()) as { csrf_token: string | null }
    csrfToken = s.csrf_token
  }
  const headers: Record<string, string> = { ...options?.headers }
  if (csrfToken) headers['x-csrf-token'] = csrfToken
  const init: RequestInit = { method, credentials: 'same-origin', headers }
  if (body !== undefined) { headers['content-type'] = 'application/json'; init.body = JSON.stringify(body) }
  const r = await fetch(path, init)
  const data = (await r.json().catch(() => ({}))) as T
  reactToStatus(path, r.status, (data as { error?: string }).error, options)
  return { status: r.status, data }
}

/** Upload binaire (branding) — mêmes garanties CSRF que apiMutate. */
export async function apiUpload<T>(method: string, path: string, file: File, options?: ApiOptions): Promise<{ status: number; data: T }> {
  if (!csrfToken) {
    const s = await fetch('/api/v1/auth/session', { credentials: 'same-origin' }).then((r) => r.json()) as { csrf_token: string | null }
    csrfToken = s.csrf_token
  }
  const headers: Record<string, string> = { 'content-type': file.type || 'application/octet-stream', ...options?.headers }
  if (csrfToken) headers['x-csrf-token'] = csrfToken
  const r = await fetch(path, { method, credentials: 'same-origin', headers, body: file })
  const data = (await r.json().catch(() => ({}))) as T
  // Un dépôt peut lui aussi tomber sur une session expirée : avec l'option,
  // la même règle que les autres appels (un upload n'y réagissait pas du tout).
  if (options?.deconnexionExplicite) reactToStatus(path, r.status, (data as { error?: string }).error, options)
  return { status: r.status, data }
}

/**
 * `signal` (facultatif) : une lecture abandonnée — écran quitté, relecture —
 * est annulée sur le réseau, pas seulement ignorée à l'arrivée (11/10/2026).
 * Annulée, elle lève comme une coupure : à l'appelant de ne plus rien écrire.
 */
export async function apiGet<T>(path: string, options?: { signal?: AbortSignal | undefined }): Promise<T> {
  const r = await fetch(path, { credentials: 'same-origin', signal: options?.signal ?? null })
  const data = (await r.json()) as T
  reactToStatus(path, r.status, (data as { error?: string }).error)
  return data
}

/**
 * Lecture qui rend AUSSI le statut HTTP (Signalements, 09/10/2026).
 *
 * `apiGet` rend le corps seul et lève quand il n'est pas du JSON : suffisant
 * tant que le code d'erreur suffit à décider. Un écran qui doit distinguer un
 * service amont indisponible (503) d'un refus (403) ou d'une fiche disparue
 * (404) — y compris quand un intermédiaire répond en HTML — lit ici :
 * `{ status, data }`, corps illisible → `{}`, jamais d'exception sur un statut.
 * Une coupure réseau, elle, lève encore : à l'appelant de la dire.
 */
export async function apiRead<T>(path: string, options?: ApiOptions): Promise<{ status: number; data: T }> {
  const r = await fetch(path, { credentials: 'same-origin', headers: { ...options?.headers } })
  const data = (await r.json().catch(() => ({}))) as T
  reactToStatus(path, r.status, (data as { error?: string }).error, options)
  return { status: r.status, data }
}

/**
 * Mutation d'administration avec REPRISE APRÈS STEP-UP (LOT 5.5.1) : sur
 * STEPUP_REQUIRED, le dialogue de ré-authentification forte s'ouvre (TOTP ou
 * clé d'accès) puis la mutation est REJOUÉE À L'IDENTIQUE — l'utilisateur ne
 * perd jamais ce qu'il était en train d'enregistrer. Le rejeu repart avec les
 * MÊMES options : une clé d'idempotence reste celle de la première tentative.
 */
export async function adminMutate<T extends { error?: string }>(
  method: string, path: string, body?: unknown, options?: ApiOptions,
): Promise<{ status: number; data: T }> {
  let r = await apiMutate<T>(method, path, body, options)
  if (r.status === 403 && r.data.error === 'STEPUP_REQUIRED') {
    const ok = await requestStepUp()
    if (ok) r = await apiMutate<T>(method, path, body, options)
  }
  return r
}

/** Variante upload binaire (branding, avatar) — même reprise step-up. */
export async function adminUpload<T extends { error?: string }>(
  method: string, path: string, file: File, options?: ApiOptions,
): Promise<{ status: number; data: T }> {
  let r = await apiUpload<T>(method, path, file, options)
  if (r.status === 403 && r.data.error === 'STEPUP_REQUIRED') {
    const ok = await requestStepUp()
    if (ok) r = await apiUpload<T>(method, path, file, options)
  }
  return r
}

/** Permissions de la session courante — pilote la VISIBILITÉ des actions
 *  (le backend reste seul décideur). */
export interface PermissionsState {
  permissions: string[]
  authenticated: boolean
  /** Vrai tant que la réponse n'est pas arrivée. « Pas encore su » ≠ « refusé ». */
  loading: boolean
}

/* UN SEUL chargement pour tout le document.
   Chaque composant appelait /api/v1/me pour son compte : autant d'occasions de
   se contredire, et autant de requêtes concurrentes au premier rendu. */
let permCache: { permissions: string[]; authenticated: boolean } | null = null
let permEnVol: Promise<{ permissions: string[]; authenticated: boolean }> | null = null
const permAbonnes = new Set<(v: { permissions: string[]; authenticated: boolean }) => void>()

function chargerPermissions(): Promise<{ permissions: string[]; authenticated: boolean }> {
  if (permCache) return Promise.resolve(permCache)
  if (permEnVol) return permEnVol
  permEnVol = apiGet<{ user: unknown; permissions: string[] }>('/api/v1/me')
    .then((j) => {
      const v = { permissions: j.permissions ?? [], authenticated: j.user !== null }
      permCache = v
      permAbonnes.forEach((f) => f(v))
      return v
    })
    .finally(() => { permEnVol = null })
  return permEnVol
}

/** À appeler quand les droits ont pu changer (connexion, élévation). */
export function invalidatePermissions(): void { permCache = null }

export function usePermissions(): PermissionsState {
  const [state, setState] = useState<PermissionsState>(() => permCache
    ? { ...permCache, loading: false }
    : { permissions: [], authenticated: false, loading: true })

  useEffect(() => {
    if (permCache) { setState({ ...permCache, loading: false }); return }
    let vivant = true
    const maj = (v: { permissions: string[]; authenticated: boolean }) => {
      if (vivant) setState({ ...v, loading: false })
    }
    permAbonnes.add(maj)
    void chargerPermissions().then(maj).catch(() => {
      // Une erreur réseau n'est pas un refus : on cesse d'afficher un
      // squelette, sans pour autant prétendre que l'utilisateur n'a rien.
      if (vivant) setState((p) => ({ ...p, loading: false }))
    })
    return () => { vivant = false; permAbonnes.delete(maj) }
  }, [])

  return state
}
